import {html, css, TemplateResult, CSSResultGroup} from 'lit'
import {customElement, property, state} from 'lit/decorators.js'
import {BaseComponent} from '../base-component.js'
import {classMap} from 'lit/directives/class-map.js'
import {animate} from '@lit-labs/motion'
import {animateHeightContainer} from '../motion/animations.js'

export enum CARD_TYPES {
  default = 'default',
  theme = 'theme',
  error = 'error',
}

@customElement('il-card-deprecated')
export default class CardComponentDeprecated extends BaseComponent {
  static override styles: CSSResultGroup = [
    BaseComponent.styles,
    css`
      .il-card {
        --il-card-bg-color: var(--il-color-neutrals-50);
        --il-card-border-color: var(--il-color-neutrals-100);
        --il-card-border-radius: 6px;
        --il-card-border-width: 1px;
        --il-card-padding: 12px 16px;

        display: flex;
        flex-direction: column;
        gap: 12px;

        border-radius: var(--il-card-border-radius);
        border: var(--il-card-border-width) solid var(--il-card-border-color);
        background-color: var(--il-card-bg-color);
        padding: 0;
      }

      .il-card.il-card--theme {
        --il-card-bg-color: var(--il-color-accent-50);
        --il-card-border-color: var(--il-color-accent-100);
        --il-card-border-radius: 12px;
        --il-card-border-width: 2px;
        --il-card-padding: 16px;
      }

      .il-card.il-card--error {
        --il-card-bg-color: var(--il-color-neutrals-50);
        --il-card-border-color: var(--il-color-warning);
        --il-card-border-radius: 12px;
        --il-card-border-width: 2px;
        --il-card-padding: 16px;

        color: var(--il-color-warning);
        font-weight: 400;
        font-size: var(--il-font-size-14);
      }

      .il-card.il-card--padding {
        padding: var(--il-card-padding);
      }

      .il-card.il-card--no-gap {
        gap: 0;
      }

      ::slotted(hr[part='divider']) {
        margin: 0;
        border: none;
        border-bottom: 1px solid var(--il-card-border-color);
      }

      .il-card.il-card--padding ::slotted(hr[part='divider']) {
        margin: 0 -16px;
      }

      .il-card--expand-up {
        flex-direction: column-reverse;
      }

      .il-card__header {
        display: flex;
        align-items: center;
        cursor: pointer;
      }

      .il-card__content {
        overflow: hidden;
        height: auto;
      }

      .il-card__content--collapsed {
        height: 0;
      }

      .il-card__expand-icon {
        width: 20px;
        height: 20px;
        margin-right: 8px;
      }

      .il-card__expand-icon--expanded {
        transform: rotate(180deg);
      }

      .il-card__expand-icon--expand-up {
        transform: rotate(180deg);
      }

      .il-card__expand-icon--expanded.il-card__expand-icon--direction-up {
        transform: rotate(0deg);
      }
    `,
  ]

  @property({type: String})
  type: CARD_TYPES = CARD_TYPES.default

  @property({attribute: 'padding', type: Boolean})
  padding: boolean = false

  @property({attribute: 'no-gap', type: Boolean})
  noGap: boolean = false

  @property({attribute: 'collapsible', type: Boolean})
  collapsible: boolean = false

  @property({attribute: 'expand-up', type: Boolean})
  expandUp: boolean = false

  @state()
  private expanded: boolean = false

  private toggleExpand (): void {
    if (!this.collapsible) return
    this.expanded = !this.expanded
  }

  private expandIcon (): TemplateResult {
    if (!this.collapsible) return html``

    const expandIconClasses = {
      'il-card__expand-icon--expanded': this.expanded,
      'il-card__expand-icon--expand-up': this.expandUp,
    }

    return html`
      <svg
        class="il-card__expand-icon ${classMap(expandIconClasses)}"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          d="M10 13.0625L5 8.0625L6.0625 7L10 10.9375L13.9375 7L15 8.0625L10 13.0625Z"
        />
      </svg>
    `
  }

  private collapsibleHeader (): TemplateResult {
    if (!this.collapsible) return html``

    return html`
      <div
        class="il-card__header"
        @click=${this.toggleExpand}
        data-test-iliad-card-collapsible-header
        data-testid="iliad-card-collapsible-header"
      >
        ${this.expandIcon()}
        <slot name="header"></slot>
      </div>
    `
  }

  override render (): TemplateResult {
    let classNames = `${this.baseCSSClass} il-card`

    if (this.type === CARD_TYPES.theme) {
      classNames = `${classNames} il-card--theme`
    }

    if (this.type === CARD_TYPES.error) {
      classNames = `${classNames} il-card--error`
    }

    if (this.padding) {
      classNames = `${classNames} il-card--padding`
    }

    if (this.noGap) {
      classNames = `${classNames} il-card--no-gap`
    }

    if (this.expandUp) {
      classNames = `${classNames} il-card--expand-up`
    }

    const contentClasses = {
      'il-card__content': true,
      'il-card__content--collapsed': this.collapsible && !this.expanded,
    }

    return html`
      <div class="${classNames}" data-test-iliad-card data-testid="iliad-card">
        ${this.collapsibleHeader()}
        <div
          class="${classMap(contentClasses)}"
          ${animate(
            animateHeightContainer({
              fast: this.fastAnimation,
              animationCurve: this.animationCurve,
            })
          )}
          data-test-iliad-card-collapsible-content
          data-testid="iliad-card-collapsible-content"
        >
          <slot></slot>
        </div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'il-card-deprecated': CardComponentDeprecated
  }
}